import { Alert } from '@designsforhealth/dfh-react-components'
import React, { FC, useEffect } from 'react'
import styled from 'styled-components'

import NotFoundPage from '../components/NotFoundPage'
import { DispensaryNotReadyMessages } from '../components/shop/account/dispensary/dashboard/messages/DispensaryNotReadyMessages'
import Spinner from '../components/Spinner'
import useLogAndCaptureError from '../hooks/useLogAndCaptureError'
import { Dispensary, getDispensaryFromData } from '../lib/dispensaries'
import { useDispensaryByCodeEager } from '../lib/dispensaries/hooks/useDispensaryByCodeEager'
import { usePractitionerSettingsEager } from '../lib/practitioner-settings/hooks/usePractitionerSettingsEager'
import { PractitionerSettings } from '../lib/practitioner-settings/types'
import { latestDispensarySlug$, setDispensarySlug } from '../lib/sanity-dispensaries/state'
import { EmptyObject } from '../utils/types'

const StyledEmail = styled.a`
  color: ${({ theme }) => theme.colors.brand.orange};
  white-space: nowrap;
`

export interface DispensaryAccountPageProps {
  settings: PractitionerSettings
  dispensary: Dispensary
}

export type WithDispensaryAccountProps<TProps = EmptyObject> = TProps & DispensaryAccountPageProps
export type WithDispensaryAccountFC<TProps = EmptyObject> = FC<WithDispensaryAccountProps<TProps>>

const withDispensaryAccount = <TProps extends Record<string, unknown>>(
  ComponentToWrap: WithDispensaryAccountFC<TProps>,
  options?: { onLoading?: () => JSX.Element },
): FC<TProps> =>
  function WithDispensaryAccount(props) {
    const {
      data: settings,
      loading: settingsLoading,
      error: settingsError,
    } = usePractitionerSettingsEager()
    useLogAndCaptureError(settingsError)
    const practitionerCode = settings?.practitioner?.partnerCode
    const {
      loading: dispLoading,
      data: dispData,
      error: dispError,
    } = useDispensaryByCodeEager({
      practitionerCode,
      skip: !!settings?.practitioner?.optedOutOfDispensary,
    })
    useLogAndCaptureError(dispError)

    // hold subscription to dispensary slug for child components
    useEffect(() => {
      const subscription = latestDispensarySlug$.subscribe()
      return () => subscription.unsubscribe()
    }, [])

    // set current dispensary slug for use with observables
    useEffect(() => {
      if (dispLoading) {
        return
      }
      setDispensarySlug(dispData?.slug)
    }, [dispData?.slug, dispLoading])

    if (settingsLoading) {
      return options?.onLoading ? options.onLoading() : <Spinner loading />
    }

    if (settingsError || !settings) {
      return <NotFoundPage />
    }

    if (settings?.practitioner?.optedOutOfDispensary) {
      return (
        <p>
          Your Virtual Dispensary has been disabled. Contact us at{' '}
          <StyledEmail href="mailto:support@designsforhealth.com" target="_blank" rel="noreferrer">
            support@designsforhealth.com
          </StyledEmail>{' '}
          to request to re-enable access to Virtual Dispensary data and commissions.
        </p>
      )
    }

    if (!settingsLoading && !practitionerCode) {
      return <DispensaryNotReadyMessages settings={settings} />
    }

    if (dispLoading) {
      return options?.onLoading ? options.onLoading() : <Spinner loading />
    }

    if (dispError || !dispData) {
      return (
        <Alert type="error">
          We were unable to load your dispensary data, please try again soon.
        </Alert>
      )
    }

    const dispensary = getDispensaryFromData(dispData)

    return <ComponentToWrap settings={settings} dispensary={dispensary} {...props} />
  }

export default withDispensaryAccount
